import { InventoryType } from "@/global/store/data/types";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { Box, Stack } from "@mui/material";
import React from "react";
import { BaseInventoryCard } from "./InventoryCards/BaseInventoryCard";
import {
  InventoryEmptyState,
  InventoryLoadingState,
} from "./choose-seats-helper";
import { VirtualizedContainer } from "@/shared/common/layouts/VirtualizedContainer";
import { useAppDispatch } from "@/global/store";
import { seatMapState } from "@/global/store/data/seatMap";
import { findSeatMapSectionIdFromMapping } from "../../InteractiveSeatMap/utils.seatMap";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

interface Props {
  inventories: InventoryType[];
  selectedInventories: InventoryType[];
  isLoading: boolean;
  onSelect?: (inv: InventoryType) => void;
  onMouseEnterInventory?: (inv: InventoryType) => void;
  onMouseLeaveInventory?: (inv: InventoryType) => void;
  isInventoriesValidated?: boolean;
  renderInventoryCard?: (inv: InventoryType) => React.ReactNode;
}

export function VirtualizedInventoryGridV2(props: Props) {
  const dispatch = useAppDispatch();
  const teamConfig = useTeamConfig();

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { md: mdDown } = useCssMediaQuery("down");
  const dimensions = {
    padding: 16,
  };

  const handleClickView3d = (section: string) => {
    const sectionId = findSeatMapSectionIdFromMapping(section, teamConfig);
    dispatch(seatMapState.actions.set({ viewId3d: `S_${sectionId}` }));
  };

  // loading state
  if (props?.isLoading) {
    return (
      <Stack
        id={"loading-state-wrapper"}
        width={"100%"}
        height={mdDown ? "auto" : "100%"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <InventoryLoadingState />
      </Stack>
    );
  }

  // empty state
  if (!props?.inventories?.length && !props?.isLoading) {
    return (
      <Stack
        width={"100%"}
        height={mdDown ? "250px" : "100%"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <InventoryEmptyState />
      </Stack>
    );
  }

  return (
    <Box
      id="virtualized-inventory-grid-renderer"
      ref={scrollContainerRef}
      mt={2}
      p={2}
      sx={{
        width: `calc(100% + ${dimensions.padding * 4}px)`,
        height: "450px",
        overflowY: "auto",
        transform: `translate(-${dimensions.padding * 2}px, -${
          dimensions.padding * 2
        }px)`,
      }}
    >
      <VirtualizedContainer
        data={props?.inventories}
        columnCount={mdDown ? 1 : 2}
        spacing={"24px"}
        // On desktop the hover footer (Select + / More Info) is absolutely
        // positioned below the card, so reserve trailing space for the last
        // row's expanded footer. Mobile keeps the footer in normal flow.
        bottomReserve={mdDown ? 0 : 200}
        containerStyle={{
          height: mdDown ? "850px" : "450px",
          padding: "16px",
        }}
        renderElement={(itm) => {
          if (props?.renderInventoryCard) {
            return props?.renderInventoryCard?.(itm);
          }

          const isSelected = props.selectedInventories
            .map((inv) => inv?.id)
            .includes(itm?.id);

          if (mdDown) {
            return (
              <BaseInventoryCard
                inventory={itm}
                isDisabled={props?.isInventoriesValidated && !isSelected}
                isDisabledHoverAnim={mdDown}
                isSelected={isSelected}
                onSelect={() => props?.onSelect && props.onSelect(itm)}
                style={{
                  wrapper: {
                    height: "max-content",
                  },
                }}
                onClickViewSeat={handleClickView3d}
              />
            );
          }

          return (
            <BaseInventoryCard
              inventory={itm}
              isDisabled={props?.isInventoriesValidated && !isSelected}
              isDisabledHoverAnim={mdDown}
              isSelected={isSelected}
              onSelect={() => props?.onSelect && props.onSelect(itm)}
              onClickViewSeat={handleClickView3d}
            />
          );
        }}
      />
    </Box>
  );
}
